/**
 * SP Vintage — Kaching Bundles bridge.
 *
 * Kaching's app embed renders deal config server-side as a JSON `<script>` tag
 * on every PDP where a deal applies. We consume that data directly:
 *
 *   script.kaching-bundles-deal-block-settings
 *     - nanoId               → `deal` field in line item property
 *     - dealBars[].id        → `bid` field on the matched tier
 *     - dealBars[].quantity  → qty threshold for tier matching (ascending)
 *     - progressiveGifts.gifts[]
 *         .id                → `gift` field on the gift line property
 *         .variantGIDs[0]    → actual gift variant id
 *         .unlockAtBar       → 1-indexed reference to dealBars
 *
 * Kaching's storefront JS also attaches its own click + submit handlers that
 * post the trigger + gifts using ITS preselected dealBar (e.g. 3-box "MOST
 * POPULAR"), which collides with our intent. We can't safely co-exist via
 * submit-level hooks because Kaching's click handlers run before our submit
 * stamp ever lands. Instead, take over at the click level: a capture-phase
 * delegated click listener on the ATC button reads the deal settings, builds
 * a single multi-item /cart/add.js POST (trigger + tier gifts, all sharing
 * one activation id), preventDefault + stopImmediatePropagation so neither
 * Kaching's handlers nor product-form-component runs. One POST, no race.
 *
 * Non-trigger PDPs (no deal-block-settings script) self-skip so the native
 * product-form-component flow continues unchanged.
 */
(function () {
  if (window.__spvKachingBridge) return;
  window.__spvKachingBridge = true;

  var DEAL_SETTINGS_SELECTOR = 'script.kaching-bundles-deal-block-settings';
  var ATC_BUTTON_SELECTOR = '.spv-main-product__cta';

  function parseScript(selector) {
    var el = document.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || '');
    } catch (e) {
      console.warn('[spv-gift-with-purchase] failed to parse', selector, e);
      return null;
    }
  }

  /** 4-char alphanumeric activation id, matching Kaching's format. */
  function nanoid4() {
    var alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    var arr = new Uint8Array(4);
    crypto.getRandomValues(arr);
    var out = '';
    for (var i = 0; i < 4; i++) out += alphabet[arr[i] % alphabet.length];
    return out;
  }

  /** "gid://shopify/ProductVariant/12345" → 12345 */
  function variantIdFromGid(gid) {
    if (!gid) return null;
    var m = String(gid).match(/ProductVariant\/(\d+)/);
    return m ? Number(m[1]) : null;
  }

  /** "gid://shopify/Product/12345" → 12345 (won't match ProductVariant gids). */
  function productIdFromGid(gid) {
    if (!gid) return null;
    var m = String(gid).match(/\/Product\/(\d+)/);
    return m ? Number(m[1]) : null;
  }

  /**
   * Map of Kaching product id → first variant id, read from the app-embed
   * `script.kaching-bundles-product` tags. A deal authors each gift either by
   * variant (`variantGIDs` populated) or by product only (`variantGIDs: null`,
   * just `productGID` — e.g. the 15kg box deal). In the product-only case the
   * gift variant isn't in the deal settings at all; it lives here. These gift
   * products are single-variant (`differentVariantsEnabled: false`), so the
   * product's first variant is the gift.
   */
  function giftVariantsByProductId() {
    var map = {};
    document.querySelectorAll('script.kaching-bundles-product').forEach(function (s) {
      var p;
      try { p = JSON.parse(s.textContent || ''); } catch (e) { return; }
      if (p && p.id != null && p.variants && p.variants.length) {
        map[String(p.id)] = p.variants[0].id;
      }
    });
    return map;
  }

  /** Highest dealBar whose `quantity` is ≤ qty. dealBars authored ascending. */
  function matchDealBar(dealBars, qty) {
    var matched = null;
    var index = -1;
    for (var i = 0; i < dealBars.length; i++) {
      if (dealBars[i].quantity <= qty) {
        matched = dealBars[i];
        index = i;
      }
    }
    return matched ? { bar: matched, index: index } : null;
  }

  function readDealSettings() {
    var settings = parseScript(DEAL_SETTINGS_SELECTOR);
    if (!settings) return null;
    var dealNanoId = settings.nanoId;
    var dealBars = settings.dealBars || [];
    var gifts = (settings.progressiveGifts && settings.progressiveGifts.gifts) || [];
    if (!dealNanoId || dealBars.length === 0 || gifts.length === 0) return null;
    return { dealNanoId: dealNanoId, dealBars: dealBars, gifts: gifts };
  }

  function getCartItemsSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        ids.push(el.dataset.sectionId);
      }
    });
    return ids.join(',');
  }

  function dispatchCartUpdate(resource, variantId) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: resource,
          sourceId: 'spv-gift-with-purchase',
          data: { source: 'spv-gift-with-purchase', variantId: String(variantId) },
        },
      })
    );
  }

  /**
   * Build trigger + gift items and POST as a single /cart/add.js call.
   * Trigger property: {deal, main:true, id, bid}
   * Gift property:    {id, deal, gift} — shared `id` with trigger so Kaching's
   *                   discount engine recognises the bundle.
   */
  async function atomicAtc(form, settings) {
    var variantInput = form.querySelector('input[name="id"]');
    var qtyInput = form.querySelector('input[name="quantity"]');
    var variantId = variantInput && variantInput.value;
    if (!variantId) return false;

    var qty = Number(qtyInput && qtyInput.value) || 1;
    var matched = matchDealBar(settings.dealBars, qty);
    if (!matched) return false;

    var activationId = nanoid4();
    var dealNanoId = settings.dealNanoId;

    var items = [{
      id: Number(variantId),
      quantity: qty,
      properties: {
        __kaching_bundles: JSON.stringify({
          deal: dealNanoId,
          main: true,
          id: activationId,
          bid: matched.bar.id,
        }),
      },
    }];

    var unlockedThruBar = matched.index + 1;
    var giftVariants = giftVariantsByProductId();
    settings.gifts.forEach(function (g) {
      if (g.unlockAtBar > unlockedThruBar) return;
      // Prefer an explicitly-authored gift variant; fall back to the gift
      // product's variant when the deal specifies only a productGID (Kaching
      // leaves variantGIDs null for product-level gift authoring — e.g. the
      // 15kg box deal). Without the fallback the gift is silently dropped.
      var giftVariantId = variantIdFromGid((g.variantGIDs || [])[0]);
      if (!giftVariantId) {
        giftVariantId = giftVariants[String(productIdFromGid(g.productGID))];
      }
      if (!giftVariantId) return;
      items.push({
        id: giftVariantId,
        quantity: g.quantity || 1,
        properties: {
          __kaching_bundles: JSON.stringify({
            id: activationId,
            deal: dealNanoId,
            gift: g.id,
          }),
        },
      });
    });

    var addUrl = (window.Theme && Theme.routes && Theme.routes.cart_add_url) || '/cart/add.js';

    try {
      var res = await fetch(addUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ items: items, sections: getCartItemsSectionIds() }),
      });
      if (!res.ok) {
        var text = await res.text();
        console.warn('[spv-gift-with-purchase] atomic atc failed', res.status, text);
        return false;
      }
      var resource = await res.json();
      dispatchCartUpdate(resource, variantId);
      return true;
    } catch (e) {
      console.warn('[spv-gift-with-purchase] atomic atc error', e);
      return false;
    }
  }

  /**
   * Capture-phase click intercept. Runs before any element-level click handler
   * (Kaching's, product-form-component's, loading-button's). When the page
   * has a Kaching deal block, we take over the whole ATC flow.
   */
  function onATCClick(event) {
    var btn = event.target.closest(ATC_BUTTON_SELECTOR);
    if (!btn) return;
    var form = btn.closest('product-form-component form');
    if (!form) return;

    var settings = readDealSettings();
    if (!settings) return; // non-trigger page — let native flow run

    event.preventDefault();
    event.stopImmediatePropagation();

    if (btn.disabled || btn.getAttribute('aria-disabled') === 'true') return;
    atomicAtc(form, settings);
  }

  // --- UI neutralization (independent of the ATC flow) ---

  /**
   * Kaching's PDP block forces the qty input to the preselected tier's qty
   * (e.g. 3 for "3 BOX"). Lock the input to the merchant default rendered by
   * Liquid. Idempotent. Uses a property-descriptor + MutationObserver because
   * Kaching writes via both .value and setAttribute.
   */
  function lockQuantityInput(form) {
    var input = form && form.querySelector('input[name="quantity"]');
    if (!input || input.__spvQuantityLocked) return;

    var merchantDefault = input.getAttribute('value') || '1';
    var descriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
    if (!descriptor || !descriptor.set) return;

    descriptor.set.call(input, merchantDefault);

    Object.defineProperty(input, 'value', {
      configurable: true,
      get: function () { return descriptor.get.call(this); },
      set: function () { descriptor.set.call(this, merchantDefault); },
    });

    new MutationObserver(function () {
      if (input.getAttribute('value') !== merchantDefault) {
        input.setAttribute('value', merchantDefault);
      }
    }).observe(input, { attributes: true, attributeFilter: ['value'] });

    input.__spvQuantityLocked = true;
  }

  /** €167,95 — matches Kaching's `amount_with_comma_separator` Liquid format. */
  function formatEuros(cents) {
    var euros = Math.floor(cents / 100);
    var rem = String(cents % 100).padStart(2, '0');
    return '€' + euros.toLocaleString('es-ES') + ',' + rem;
  }

  /**
   * Kaching also (1) adds `kaching-bundles-form--*` class that disables the
   * ATC button, and (2) rewrites the CTA price span to "qty × unit price".
   * The MutationObserver below reverts both as Kaching applies them — its
   * takeover is one-shot per init, so one revert per attribute change is enough.
   */
  function startNeutralizationGuard(form) {
    if (!form || form.__spvKachingNeutralized) return;
    form.__spvKachingNeutralized = true;

    var btn = form.querySelector(ATC_BUTTON_SELECTOR);
    var ctaTotal = form.querySelector('.spv-main-product__cta-total');
    var product = parseScript('script.kaching-bundles-product[data-main="true"]')
               || parseScript('script.kaching-bundles-product');
    var variantInput = form.querySelector('input[name="id"]');

    function expectedPrice() {
      if (!product || !variantInput) return null;
      var v = product.variants && product.variants.find(function (x) {
        return String(x.id) === String(variantInput.value);
      });
      return v && typeof v.price === 'number' ? formatEuros(v.price) : null;
    }

    var reverting = false;
    function revert() {
      if (reverting) return;
      reverting = true;
      try {
        Array.from(form.classList).forEach(function (cls) {
          if (cls.indexOf('kaching-bundles-form') === 0) form.classList.remove(cls);
        });
        if (btn && btn.hasAttribute('disabled')) {
          btn.removeAttribute('disabled');
          btn.disabled = false;
        }
        if (ctaTotal) {
          var expected = expectedPrice();
          if (expected && ctaTotal.textContent.trim() !== expected) {
            ctaTotal.textContent = expected;
          }
        }
      } finally {
        reverting = false;
      }
    }

    revert();
    var obs = new MutationObserver(revert);
    obs.observe(form, { attributes: true, attributeFilter: ['class'] });
    if (btn) obs.observe(btn, { attributes: true, attributeFilter: ['disabled'] });
    if (ctaTotal) obs.observe(ctaTotal, { childList: true, characterData: true, subtree: true });
  }

  function neutralizeAllProductForms() {
    document.querySelectorAll('product-form-component form').forEach(function (form) {
      lockQuantityInput(form);
      startNeutralizationGuard(form);
    });
  }

  function init() {
    // Click interception in capture phase: runs before Kaching's listeners and
    // before product-form-component's on:click binding, so a single atomic POST
    // replaces the multi-POST race entirely.
    document.addEventListener('click', onATCClick, true);

    // Neutralize Kaching's UI takeover (qty input, button disabled, price text).
    neutralizeAllProductForms();
    // spv-main-product.js innerHTML-swaps the section on grouping-card clicks
    // and dispatches `spv:group-swap` afterwards, at which point Kaching has
    // re-applied its takeover to the new form's children.
    document.addEventListener('spv:group-swap', neutralizeAllProductForms);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
